import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LEGAL_ADDRESS,
  LEGAL_BUSINESS_NAME,
  LEGAL_CONTACT_EMAIL,
  LEGAL_DOMAIN,
  LEGAL_SITE_NAME,
  LEGAL_SITE_URL,
  LEGAL_TAX_ID,
  LEGAL_URLS,
} from '../../config/legal';
import { CONTACT_WHATSAPP_DISPLAY } from '../../config/contact';
import { LegalPageShell } from './LegalPageShell';

export function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = `Política de privacidad | ${LEGAL_SITE_NAME}`;
  }, []);

  return (
    <LegalPageShell title="Política de privacidad">
      <p>
        En esta Política de privacidad explicamos cómo <strong>{LEGAL_SITE_NAME}</strong> (
        {LEGAL_SITE_URL}) recopila, utiliza, conserva y protege los datos personales de quienes
        utilizan nuestro software de punto de venta (TPV), el panel web, el chat de soporte, la
        mensajería de WhatsApp Business y los servicios relacionados.
      </p>
      <p>
        El responsable del tratamiento es <strong>{LEGAL_BUSINESS_NAME}</strong> (NIF{' '}
        {LEGAL_TAX_ID}), con domicilio en {LEGAL_ADDRESS}, titular del servicio {LEGAL_SITE_NAME}{' '}
        ({LEGAL_SITE_URL}). Para cualquier cuestión sobre privacidad puedes escribir a{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
      </p>

      <h2>1. Ámbito de aplicación</h2>
      <p>Esta política se aplica a:</p>
      <ul>
        <li>Clientes registrados en el panel web de {LEGAL_SITE_NAME}.</li>
        <li>Usuarios del TPV de escritorio vinculado a una licencia.</li>
        <li>
          Usuarios de nuestras aplicaciones móviles <strong>Hosty</strong> (cliente) y{' '}
          <strong>Hosty Staff</strong> para Android e iOS.
        </li>
        <li>Personas que contactan con nosotros por formulario web, chat integrado o WhatsApp.</li>
        <li>Agentes de soporte y personal autorizado que accede a las herramientas staff o ERP.</li>
        <li>Visitantes de la web {LEGAL_DOMAIN}.</li>
      </ul>
      <p>
        Si utilizas nuestra integración con Meta (WhatsApp Business Platform), también se aplican
        las condiciones de Meta para el tratamiento de datos de la plataforma, sin perjuicio de lo
        aquí descrito.
      </p>

      <h2>2. Datos que recopilamos</h2>
      <h3>2.1. Cuenta y licencias</h3>
      <ul>
        <li>
          Nombre, correo electrónico, contraseña (almacenada de forma cifrada), tipo de titular y
          NIF del titular o representante legal.
        </li>
        <li>Datos de facturación y suscripción gestionados a través de Stripe.</li>
        <li>Información de licencias, dispositivos vinculados y estado del servicio.</li>
      </ul>

      <h3>2.2. Datos operativos del TPV</h3>
      <ul>
        <li>Informes de ventas, cierres de caja, tickets y datos agregados sincronizados con la nube.</li>
        <li>Configuración del negocio necesaria para prestar el servicio.</li>
        <li>Datos de facturación electrónica (Verifactu), cuando el cliente activa ese módulo.</li>
      </ul>

      <p>
        En los cobros de la carta digital conservamos el importe, la propina, el estado y los
        identificadores técnicos del pago y de la cuenta conectada. Los datos completos de tarjeta
        o cuenta bancaria los recopila y conserva Stripe, no Hosty.
      </p>

      <h3>2.3. Soporte, chat y WhatsApp</h3>
      <ul>
        <li>Nombre, teléfono, correo y contenido de los mensajes que nos envíes.</li>
        <li>
          En WhatsApp Business: número de teléfono, nombre de perfil de WhatsApp, identificadores
          de conversación y mensajes intercambiados con nuestro equipo de soporte.
        </li>
        <li>Metadatos técnicos asociados (fecha, estado de entrega o lectura, identificadores de mensaje).</li>
      </ul>

      <h3>2.4. Datos técnicos</h3>
      <ul>
        <li>Dirección IP, tipo de navegador, cookies de sesión y registros de seguridad.</li>
        <li>Tokens de autenticación y datos necesarios para mantener la sesión iniciada.</li>
      </ul>

      <h3>2.5. Aplicaciones móviles</h3>
      <ul>
        <li>
          Identificador de notificaciones push (token de Expo / Firebase Cloud Messaging) y modelo
          del dispositivo, asociados a tu cuenta para enviarte avisos de soporte y mensajes nuevos.
        </li>
        <li>
          Solo recopilamos el token push si concedes el permiso de notificaciones; puedes revocarlo
          en cualquier momento desde los ajustes del sistema o de la aplicación.
        </li>
      </ul>

      <h2>3. Finalidades del tratamiento</h2>
      <p>Tratamos los datos personales para:</p>
      <ul>
        <li>Prestar, mantener y mejorar el software TPV y los servicios en la nube.</li>
        <li>Gestionar cuentas, licencias, pagos y soporte al cliente.</li>
        <li>Atender consultas por chat web o WhatsApp y gestionar tickets de soporte.</li>
        <li>Cumplir obligaciones legales, contables o fiscales aplicables.</li>
        <li>Garantizar la seguridad del servicio y prevenir usos fraudulentos.</li>
        <li>Enviar comunicaciones operativas relacionadas con el servicio contratado.</li>
      </ul>

      <h2>4. Base legal (RGPD)</h2>
      <ul>
        <li>
          <strong>Ejecución de contrato:</strong> registro, licencia, sincronización de informes y
          facturación de la suscripción.
        </li>
        <li>
          <strong>Interés legítimo:</strong> seguridad, mejora del servicio y atención de solicitudes
          de soporte.
        </li>
        <li>
          <strong>Consentimiento:</strong> cuando nos contactas voluntariamente o aceptas comunicaciones
          no estrictamente necesarias.
        </li>
        <li>
          <strong>Obligación legal:</strong> conservación de información exigida por normativa fiscal o
          mercantil.
        </li>
      </ul>

      <h2>5. Destinatarios y encargados del tratamiento</h2>
      <p>Podemos compartir datos con proveedores que nos ayudan a operar el servicio, entre ellos:</p>
      <ul>
        <li>
          <strong>Meta Platforms</strong> (WhatsApp Business Platform), para mensajería con clientes
          y soporte.
        </li>
        <li>
          <strong>Stripe</strong>, para pagos y suscripciones.
        </li>
        <li>
          <strong>Verifacti</strong>, cuando el cliente utiliza el módulo Verifactu.
        </li>
        <li>
          <strong>Google</strong> (Firebase Cloud Messaging) y <strong>Expo</strong>, para la entrega
          de notificaciones push en las aplicaciones móviles.
        </li>
        <li>Proveedores de hosting, correo electrónico y infraestructura técnica.</li>
      </ul>
      <p>
        Exigimos a estos proveedores medidas de seguridad adecuadas y, cuando corresponda, acuerdos
        de tratamiento conforme al RGPD.
      </p>

      <h2>6. Transferencias internacionales</h2>
      <p>
        Algunos proveedores pueden tratar datos fuera del Espacio Económico Europeo. En esos casos
        aplicamos las garantías previstas por la normativa aplicable (cláusulas contractuales tipo,
        decisiones de adecuación u otras medidas reconocidas).
      </p>

      <h2>7. Plazos de conservación</h2>
      <ul>
        <li>Datos de cuenta: mientras mantengas la relación contractual y el plazo legal posterior.</li>
        <li>Tickets de soporte y WhatsApp: el tiempo necesario para resolver la consulta y plazos legales.</li>
        <li>Datos de facturación: según obligaciones contables y fiscales.</li>
        <li>Registros técnicos: plazos limitados por seguridad y diagnóstico.</li>
      </ul>

      <h2>8. Tus derechos</h2>
      <p>
        Puedes ejercer los derechos de acceso, rectificación, supresión, limitación, oposición y
        portabilidad, así como retirar el consentimiento cuando el tratamiento se base en él.
      </p>
      <p>
        También puedes presentar una reclamación ante la Agencia Española de Protección de Datos (
        <a href="https://www.aepd.es" rel="noopener noreferrer" target="_blank">
          www.aepd.es
        </a>
        ).
      </p>
      <p>
        Para ejercer tus derechos, escribe a{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a> indicando tu solicitud
        y un medio para verificar tu identidad.
      </p>

      <h2>9. Eliminación de datos</h2>
      <p>
        Puedes solicitar la eliminación de tus datos personales en cualquier momento. Consulta las
        instrucciones detalladas en nuestra página de{' '}
        <Link to={LEGAL_URLS.dataDeletion}>eliminación de datos</Link>.
      </p>
      <p>
        Si nos has escrito por WhatsApp al {CONTACT_WHATSAPP_DISPLAY}, incluye en tu solicitud el
        número desde el que nos contactaste para localizar la conversación.
      </p>

      <h2>10. Seguridad</h2>
      <p>
        Aplicamos medidas técnicas y organizativas razonables para proteger los datos frente a
        accesos no autorizados, pérdida o alteración, incluyendo cifrado en tránsito (HTTPS),
        control de acceso y buenas prácticas en el almacenamiento de credenciales.
      </p>

      <h2>11. Menores de edad</h2>
      <p>
        {LEGAL_SITE_NAME} está dirigido a profesionales y empresas de hostelería. No recopilamos
        intencionadamente datos de menores de 14 años.
      </p>

      <h2>12. Cambios en esta política</h2>
      <p>
        Podemos actualizar esta Política de privacidad para reflejar cambios legales o del servicio.
        Publicaremos la versión vigente en {LEGAL_SITE_URL}
        {LEGAL_URLS.privacy}.
      </p>
    </LegalPageShell>
  );
}
